/**
 * WorkspaceGuard — route-level workspace enforcement
 *
 * Checks if the current user has access to the required workspace(s).
 * This mirrors the backend require_workspace() dependency.
 *
 * Workspace determination (same logic as backend):
 * 1. role="admin" → all workspaces
 * 2. user.user_roles list (explicit assignment)
 * 3. ROLE_WORKSPACE_DEFAULTS[user.role] (default access)
 *
 * Usage:
 *   <WorkspaceGuard workspace="fi">
 *     <FIPage />
 *   </WorkspaceGuard>
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield } from "lucide-react";

// Default workspace access by role — mirrors backend ROLE_WORKSPACE_DEFAULTS
export const ROLE_WORKSPACE_DEFAULTS = {
  sme:            ["sme", "training"],
  project:        ["project", "training"],
  corporate:      ["corporate", "training"],
  fi:             ["fi", "knowledge"],
  states:         ["states", "training"],
  training:       ["training"],
  user:           ["sme", "training"],
  verifier:       ["project"],
  lcfpi:          ["lcfpi"],
  lcfpi_gov:      ["lcfpi"],
  lcfpi_pm:       ["lcfpi"],
  lcfpi_investor: ["lcfpi"],
  investor:       ["lcfpi"],   // LCFPI investor / DFI read-only users
  admin:          null,        // null = all workspaces
};

export function hasWorkspaceAccess(user, workspace) {
  if (!user) return false;
  if (user.role === "admin") return true;

  // Check explicit user_roles list
  if (user.user_roles && user.user_roles.includes(workspace)) return true;

  // Check default access from platform role
  const defaults = ROLE_WORKSPACE_DEFAULTS[user.role] || ROLE_WORKSPACE_DEFAULTS.user;
  if (defaults && defaults.includes(workspace)) return true;

  return false;
}

export default function WorkspaceGuard({ workspace, children, redirectTo = "/dashboard" }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  if (!hasWorkspaceAccess(user, workspace)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F8FAFC]">
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center max-w-md">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Shield size={22} className="text-slate-400" />
          </div>
          <h2 className="text-lg font-bold text-[#0F172A] mb-2">Workspace Access Restricted</h2>
          <p className="text-sm text-slate-400 mb-1">
            You don't have access to the <strong>{workspace}</strong> workspace.
          </p>
          <p className="text-xs text-slate-400 mb-6">
            Contact your administrator to request access, or navigate to your assigned workspace.
          </p>
          <a href={redirectTo}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F172A] text-white text-sm font-medium rounded-xl hover:bg-slate-700">
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return children;
}
